/**
 * Product access API — grants, invites, requests via pro-api BFF.
 */

import { authenticatedProApiFetch, proApiFetch } from './pro-api.client';

export type ProductAccessTier = 'view' | 'edit' | 'full';
export type ProductAccessTierOrCustom = ProductAccessTier | 'custom';

export const PRODUCT_ACCESS_TIER_OPTIONS: Array<{
  value: ProductAccessTier;
  label: string;
}> = [
  { value: 'view', label: 'View Access' },
  { value: 'edit', label: 'Edit Access' },
  { value: 'full', label: 'Full Access' },
];

export type ProductAccessSource = {
  kind: string;
  label: string;
  organizationId?: string;
  organizationName?: string;
  roleName?: string | null;
  roleLabel?: string | null;
  permissions?: string[];
  tier?: ProductAccessTierOrCustom;
};

export type EffectiveProductAccess = {
  productId: string;
  tier: ProductAccessTierOrCustom | null;
  canManage: boolean;
  sources: ProductAccessSource[];
  owningOrganization?: { id: string; name: string | null; logoUrl?: string | null };
};

export type ProductAccessGrantRow = {
  userId: string;
  productId: string;
  tier: ProductAccessTier;
  email: string;
  displayName: string | null;
  firstName: string | null;
  lastName: string | null;
  avatarUrl: string | null;
  createdAt: string;
  sourceKind: 'direct';
};

export type ProductAccessInviteRow = {
  inviteId: string;
  productId: string;
  email: string;
  tier: ProductAccessTier;
  expiresAt: string;
  createdAt: string;
  sourceKind: 'invite';
};

export type ProductAccessRequestRow = {
  userId: string;
  productId: string;
  email: string;
  displayName: string | null;
  firstName: string | null;
  lastName: string | null;
  avatarUrl: string | null;
  requestedTier?: ProductAccessTier;
  createdAt: string;
  sourceKind: 'request';
};

export type ProductAccessInheritedRow = {
  userId: string;
  email: string;
  displayName: string | null;
  firstName: string | null;
  lastName: string | null;
  avatarUrl: string | null;
  tier: ProductAccessTierOrCustom | null;
  roleName: string | null;
  roleLabel: string | null;
  roleColor: string | null;
  organizationId: string;
  organizationName: string;
  permissions: string[];
  sourceKind: 'inherited';
};

export type ProductAccessListResponse = {
  grants: ProductAccessGrantRow[];
  invites: ProductAccessInviteRow[];
  requests: ProductAccessRequestRow[];
  inherited: ProductAccessInheritedRow[];
};

export type ProductAccessPendingRequest = {
  productId: string;
  status: string;
  requestedTier?: ProductAccessTier;
  createdAt: string;
};

export type ProductAccessCheckResponse = {
  allowed: boolean;
  canManage: boolean;
  access?: EffectiveProductAccess;
  pendingRequest?: ProductAccessPendingRequest | null;
  product?: { id: string; name?: string };
};

export type ProductInvitePreview = {
  valid: boolean;
  expired: boolean;
  email?: string;
  productId?: string;
  tier?: ProductAccessTier;
};

export type ProductAccessListRowKind = 'active' | 'pending' | 'invited' | 'inherited';

export type ProductAccessListRow = {
  id: string;
  kind: ProductAccessListRowKind;
  userId?: string;
  inviteId?: string;
  email: string;
  displayName: string;
  firstName: string;
  lastName: string;
  avatarUrl: string | null;
  /** Current access level (grant/inherited). Null when the user has none yet. */
  tier: ProductAccessTierOrCustom | null;
  /** For pending requests: the tier they are asking for. */
  requestedTier?: ProductAccessTier | null;
  tierEditable: boolean;
  sourceLabel: string;
  roleColor?: string | null;
};

function displayNameFrom(
  displayName: string | null | undefined,
  firstName: string | null | undefined,
  lastName: string | null | undefined,
  email: string,
): string {
  if (displayName?.trim()) return displayName.trim();
  const joined = [firstName, lastName].filter(Boolean).join(' ').trim();
  if (joined) return joined;
  return email;
}

export function toProductAccessListRows(
  data: ProductAccessListResponse,
): ProductAccessListRow[] {
  const grantByUserId = new Map(data.grants.map((g) => [g.userId, g]));
  const inheritedByUserId = new Map(data.inherited.map((row) => [row.userId, row]));
  const pendingUserIds = new Set(data.requests.map((r) => r.userId));

  const pending: ProductAccessListRow[] = data.requests.map((r) => {
    const grant = grantByUserId.get(r.userId);
    const inherited = inheritedByUserId.get(r.userId);
    const currentTier = (grant?.tier ?? inherited?.tier ?? null) as
      | ProductAccessTierOrCustom
      | null;
    return {
      id: `request:${r.userId}`,
      kind: 'pending' as const,
      userId: r.userId,
      email: r.email,
      displayName: displayNameFrom(r.displayName, r.firstName, r.lastName, r.email),
      firstName: r.firstName ?? '',
      lastName: r.lastName ?? '',
      avatarUrl: r.avatarUrl,
      tier: currentTier,
      requestedTier: r.requestedTier ?? 'view',
      tierEditable: false,
      sourceLabel: 'Access requested',
    };
  });

  const invited: ProductAccessListRow[] = data.invites.map((inv) => ({
    id: `invite:${inv.inviteId}`,
    kind: 'invited',
    inviteId: inv.inviteId,
    email: inv.email,
    displayName: inv.email,
    firstName: '',
    lastName: '',
    avatarUrl: null,
    tier: inv.tier,
    tierEditable: false,
    sourceLabel: 'Pending invite',
  }));

  const active: ProductAccessListRow[] = data.grants
    // Change-requests are shown on the pending row (with current tier + CTA).
    .filter((g) => !pendingUserIds.has(g.userId))
    .map((g) => ({
      id: `grant:${g.userId}`,
      kind: 'active' as const,
      userId: g.userId,
      email: g.email,
      displayName: displayNameFrom(g.displayName, g.firstName, g.lastName, g.email),
      firstName: g.firstName ?? '',
      lastName: g.lastName ?? '',
      avatarUrl: g.avatarUrl,
      tier: g.tier,
      tierEditable: true,
      sourceLabel: 'Shared directly on this product',
    }));

  const inherited: ProductAccessListRow[] = data.inherited
    .filter((row) => !grantByUserId.has(row.userId) && !pendingUserIds.has(row.userId))
    .map((row) => ({
      id: `inherited:${row.userId}`,
      kind: 'inherited' as const,
      userId: row.userId,
      email: row.email,
      displayName: displayNameFrom(
        row.displayName,
        row.firstName,
        row.lastName,
        row.email,
      ),
      firstName: row.firstName ?? '',
      lastName: row.lastName ?? '',
      avatarUrl: row.avatarUrl,
      tier: row.tier,
      tierEditable: true,
      sourceLabel: `Inherited from ${row.roleLabel ?? row.roleName ?? 'role'} (${row.organizationName})${
        row.permissions.length
          ? ` — ${row.permissions.join(', ')}`
          : ''
      }`,
      roleColor: row.roleColor,
    }));

  return [...pending, ...invited, ...active, ...inherited];
}

export async function fetchProductAccessCheck(
  accessToken: string,
  productId: string,
): Promise<ProductAccessCheckResponse> {
  return authenticatedProApiFetch(
    `/products/${productId}/access-check`,
    accessToken,
  );
}

export async function fetchProductAccessList(
  accessToken: string,
  productId: string,
): Promise<ProductAccessListResponse> {
  return authenticatedProApiFetch(
    `/product-access/products/${productId}`,
    accessToken,
  );
}

export async function inviteProductAccess(
  accessToken: string,
  productId: string,
  body: { email: string; tier: ProductAccessTier; productName?: string },
) {
  return authenticatedProApiFetch(
    `/product-access/products/${productId}/invites`,
    accessToken,
    { method: 'POST', body: JSON.stringify(body) },
  );
}

export async function cancelProductAccessInvite(
  accessToken: string,
  productId: string,
  inviteId: string,
) {
  return authenticatedProApiFetch(
    `/product-access/products/${productId}/invites/${inviteId}`,
    accessToken,
    { method: 'DELETE' },
  );
}

export async function upsertProductAccessGrant(
  accessToken: string,
  productId: string,
  body: { userId?: string; email?: string; tier: ProductAccessTier },
) {
  return authenticatedProApiFetch(
    `/product-access/products/${productId}/grants`,
    accessToken,
    { method: 'POST', body: JSON.stringify(body) },
  );
}

export async function updateProductAccessGrant(
  accessToken: string,
  productId: string,
  userId: string,
  tier: ProductAccessTier,
) {
  return authenticatedProApiFetch(
    `/product-access/products/${productId}/grants/${userId}`,
    accessToken,
    { method: 'PATCH', body: JSON.stringify({ tier }) },
  );
}

export async function removeProductAccessGrant(
  accessToken: string,
  productId: string,
  userId: string,
) {
  return authenticatedProApiFetch(
    `/product-access/products/${productId}/grants/${userId}`,
    accessToken,
    { method: 'DELETE' },
  );
}

export async function requestProductAccess(
  accessToken: string,
  productId: string,
  tier: ProductAccessTier = 'view',
) {
  return authenticatedProApiFetch(
    `/product-access/products/${productId}/requests`,
    accessToken,
    { method: 'POST', body: JSON.stringify({ tier }) },
  );
}

export async function cancelProductAccessRequest(
  accessToken: string,
  productId: string,
) {
  return authenticatedProApiFetch(
    `/product-access/products/${productId}/requests`,
    accessToken,
    { method: 'DELETE' },
  );
}

export async function approveProductAccessRequest(
  accessToken: string,
  productId: string,
  userId: string,
  tier: ProductAccessTier = 'view',
) {
  return authenticatedProApiFetch(
    `/product-access/products/${productId}/requests/${userId}/approve`,
    accessToken,
    { method: 'POST', body: JSON.stringify({ tier }) },
  );
}

export async function denyProductAccessRequest(
  accessToken: string,
  productId: string,
  userId: string,
) {
  return authenticatedProApiFetch(
    `/product-access/products/${productId}/requests/${userId}/deny`,
    accessToken,
    { method: 'POST' },
  );
}

export async function fetchProductInvitePreview(
  token: string,
): Promise<ProductInvitePreview> {
  return proApiFetch(
    `/product-invites/preview?token=${encodeURIComponent(token)}`,
  );
}

export async function acceptProductInvite(
  accessToken: string,
  token: string,
): Promise<{
  productId: string;
  tier: ProductAccessTier;
  alreadyHadAccess?: boolean;
}> {
  return authenticatedProApiFetch(
    `/product-invites/accept`,
    accessToken,
    { method: 'POST', body: JSON.stringify({ token }) },
  );
}

export async function claimProductInvite(
  accessToken: string,
  token: string,
) {
  return authenticatedProApiFetch(
    `/product-invites/claim`,
    accessToken,
    { method: 'POST', body: JSON.stringify({ token }) },
  );
}

export type MyProductAccess = {
  isSuperAdmin: boolean;
  organizationTiers: Array<{ organizationId: string; tier: ProductAccessTier }>;
  grants: Array<{ productId: string; tier: ProductAccessTier }>;
};

export async function fetchMyProductAccess(accessToken: string): Promise<MyProductAccess> {
  const data = await authenticatedProApiFetch<Partial<MyProductAccess>>(
    '/product-access/me',
    accessToken,
  );
  return {
    isSuperAdmin: Boolean(data.isSuperAdmin),
    organizationTiers: data.organizationTiers ?? [],
    grants: data.grants ?? [],
  };
}
